// Maps an event participant (uploaded entry-list row) onto the ePCR
// demographics section after the clinician confirms the match.
// NOTE: mirrored at mobile/src/components/EPCR/participantMapping.js —
// keep the two files in sync.

export const mapGender = (raw) => {
  const gender = String(raw || '').trim().toLowerCase()
  if (!gender) return undefined
  if (gender === 'm' || gender === 'male') return 'Male'
  if (gender === 'f' || gender === 'female') return 'Female'
  if (['non-binary', 'nonbinary', 'nb', 'x'].includes(gender)) return 'Non-binary'
  return 'Unknown'
}

const hasValue = (value) => String(value ?? '').trim() !== ''

// Returns only the fields to overwrite; empty participant values leave the
// existing demographics untouched. Medical notes/allergies/medications are
// deliberately NOT mapped — they are shown in the confirm dialog only.
export const mapParticipantToDemographics = (participant, prev = {}) => {
  const next = { participantNumber: participant.number }

  if (hasValue(participant.firstName)) {
    next.firstName = participant.firstName
    // Mirrors the typing behaviour: preferredName follows firstName until
    // it has been set to something else
    if (!hasValue(prev.preferredName)) next.preferredName = participant.firstName
  }
  if (hasValue(participant.lastName)) {
    next.lastName = participant.lastName
    next.lastNameUnknown = false
  }
  if (participant.dob) {
    next.dob = participant.dob
    next.dobUnknown = false
    next.dobUnknownButPaediatric = false
  }
  const gender = mapGender(participant.gender)
  if (gender) next.gender = gender

  if (hasValue(participant.addressLine1)) next.addressLine1 = participant.addressLine1
  if (hasValue(participant.addressLine2)) next.addressLine2 = participant.addressLine2
  // IE entry lists put the locality/town in address line 3 and have no
  // dedicated town column
  if (hasValue(participant.addressLine3)) next.town = participant.addressLine3
  if (hasValue(participant.county)) next.city = participant.county
  if (hasValue(participant.postCode)) next.postCode = participant.postCode

  if (hasValue(participant.contactNumber)) {
    next.contactNumber = participant.contactNumber
    if (!hasValue(prev.numberForFeedback)) {
      next.numberForFeedback = participant.contactNumber
    }
  }
  if (hasValue(participant.emergencyContactName)) {
    next.nokName = participant.emergencyContactName
  }
  if (hasValue(participant.emergencyContactNumber)) {
    next.nokContactNumber = participant.emergencyContactNumber
  }

  return next
}

// Label/value rows for the confirmation dialog, shared so web and mobile
// show the same details. Empty values are filtered out.
export const participantDialogRows = (participant, formatDate) => {
  const address = [
    participant.addressLine1,
    participant.addressLine2,
    participant.addressLine3,
    participant.county,
    participant.postCode,
  ]
    .filter(hasValue)
    .join(', ')
  const name = [participant.firstName, participant.lastName]
    .filter(hasValue)
    .join(' ')

  return [
    ['Bib Number', participant.number],
    ['Name', name],
    ['Date of Birth', participant.dob ? formatDate(participant.dob) : null],
    ['Gender', participant.gender],
    ['Club', participant.club],
    ['Mobile', participant.contactNumber],
    ['Address', address],
    [
      'Emergency Contact',
      [participant.emergencyContactName, participant.emergencyContactNumber]
        .filter(hasValue)
        .join(' — '),
    ],
    ['Medical Notes', participant.medicalNotes],
    ['Allergies', participant.allergies],
    ['Medications', participant.medications],
  ].filter(([, value]) => hasValue(value))
}
